import { createSelector } from 'reselect';
import { RootState } from '../../core/store';
import { IFilterForm, ScopeOptions } from './filterTypes';
import { remove, uniq, sortBy } from 'lodash'
import { FilterWrapper } from './types/classes/FilterWrapper';
import { Sector } from './types/classes/Sector';
import { SectorFilterOptions } from './types/enums';
import { SectorUIFields } from './helpers/Sector-Helper';
import { selectSubscribedSectors, selectUserId, selectSubscribedMsiVessels, selectIsMsi } from '../../user-management/appSelectors';

const selectFilterSlice = (state: RootState) => state.filterSlice;

export const selectSubscribedSectorCodeList = createSelector(
    [selectSubscribedSectors],
    (subscribedSectors) => subscribedSectors?.map(s => s.sectorCode) ?? [] as string[]
);

export const selectFiltersBelongingToUser = createSelector(
    [selectFilterSlice],
    (filterSlice) => filterSlice.filters.filter(k => k.scope === ScopeOptions.Personal).map((p) => ({ id: p.id, name: p.name, scope: p.scope, isDraft: p.isDraft, tempId: p.tempId, isOwnFilter: true, filters: new FilterWrapper(p.ownershipFilter ?? '[]', p.sectorFilter ?? '[]') })) as IFilterForm[]
);

export const selectFiltersSharedWithUser = createSelector(
    [selectFilterSlice, selectUserId],
    (filterSlice, userId) => filterSlice.filters.filter(k => k.scope === ScopeOptions.Team).map((p) => ({ id: p.id, name: p.name, scope: p.scope, isDraft: p.isDraft, tempId: p.tempId, isOwnFilter: p.createdById === userId, filters: new FilterWrapper(p.ownershipFilter ?? '[]', p.sectorFilter ?? '[]') })) as IFilterForm[]
);

export const selectEmptyFilter = createSelector(
    [selectSubscribedSectorCodeList],
    (subscribedSectorCodeList) => {
        const sectorFilter = subscribedSectorCodeList.map(sectorCode => new Sector(sectorCode, SectorFilterOptions.ALL, []));
        return new FilterWrapper("[]", JSON.stringify(sectorFilter))
    }
);

export const selectSelectedFilter = createSelector(
    [selectFilterSlice, selectUserId, selectSubscribedSectorCodeList],
    (filterSlice, userId, subscribedSectorCodeList) => {
        if (filterSlice.selectedFilter) {
            return {
                id: filterSlice.selectedFilter.id, name: filterSlice.selectedFilter.name, scope: filterSlice.selectedFilter.scope,
                isDraft: filterSlice.selectedFilter.isDraft,
                tempId: filterSlice.selectedFilter.tempId,
                isOwnFilter: filterSlice.selectedFilter.createdById === userId ? true : false,
                filters: new FilterWrapper(filterSlice.selectedFilter.ownershipFilter ?? '[]', filterSlice.selectedFilter.sectorFilter ?? '[]')
            } as IFilterForm;
        }
        else {
            const sectorFilter = subscribedSectorCodeList.map(sectorCode => new Sector(sectorCode, SectorFilterOptions.ALL, []));
            return {
                id: null, name: '', scope: '',
                isOwnFilter: true,
                filters: new FilterWrapper("[]", JSON.stringify(sectorFilter))
            } as IFilterForm;
        }
    });

export const selectFilterNames = createSelector(
    [selectFilterSlice, selectSelectedFilter],
    // it excludes the filter name from the list of filter names if the filter is selected - draft or update
    (filterSlice, selectedFilter) => selectedFilter.id !== null || (selectedFilter.id === null && selectedFilter.isDraft === true) ?
        remove(filterSlice.filters.map(f => f.name), r => r !== selectedFilter.name) : filterSlice.filters.map(f => f.name) as string[]
);

export const selectSectorDynamicUIFields = createSelector(
    [selectSubscribedSectorCodeList, selectIsMsi],
    (subscribedSectorCodeList, isMsi) => {
        return SectorUIFields(isMsi).filter((s: { sectorCode: string; }) => subscribedSectorCodeList.includes(s.sectorCode));
    }
);

export const selectIsFiltersLoading = createSelector(
    selectFilterSlice,
    (filterSlice) => filterSlice.isFiltersLoading
);

export const selectIsFilterBusy = createSelector(
    selectFilterSlice,
    (filterSlice) => filterSlice.isBusy
);

export const selectFlagStateListSelector = createSelector(
    [selectSubscribedMsiVessels],
    (subscribedMsiVessels) => {
        return sortBy(uniq(subscribedMsiVessels.map(s => s.flag))) as string[];
    }
);